import fragmentRegistry, { registerFragments } from '@/api/graphql/definitions/fragmentRegistry';
import { ApolloClient, InMemoryCache } from '@apollo/client/core';
import getCombinedLink from '@/api/graphql/getCombinedLink';

// Store all ApolloClients in a dictionary
const apolloClients = {};

const getApolloClient = ({ baseDomain = '' } = {}) => {
  if (!(baseDomain in apolloClients)) {
    // create new one if not exist
    apolloClients[baseDomain] = {
      client: new ApolloClient({
        link: getCombinedLink(baseDomain), // connections to server
        cache: new InMemoryCache({
          fragments: fragmentRegistry, // instantiate fragments
        }),
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
          subscribe: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
        },
      }),
      activeSubscriptions: {},
    };

    registerFragments(); // lazy register fragments for interpolation
  }

  // return existing one
  return apolloClients[baseDomain].client;
};

const getActiveSubscriptions = ({ baseDomain }) => apolloClients[baseDomain]?.activeSubscriptions;

const trackSubscription = ({ baseDomain, subscription, operation }) => {
  if (subscription) {
    const timestamp = Date.now();
    const subscriptionName = operation.query.definitions[0].name.value ?? 'sub';
    const subscriptionId = [subscriptionName, timestamp, Math.random()].join('_');

    const trackedSubscription = {
      id: subscriptionId,
      subscription,
      timestamp,
      variables: operation.variables,
    };

    apolloClients[baseDomain].activeSubscriptions[subscriptionId] = trackedSubscription;

    return {
      trackedSubscription,
      activeSubscriptions: apolloClients[baseDomain].activeSubscriptions,
    };
  }

  return {};
};

const untrackSubscription = ({ baseDomain, subscriptionId }) => {
  if (!apolloClients[baseDomain]) return;

  const { activeSubscriptions } = apolloClients[baseDomain];
  activeSubscriptions?.[subscriptionId]?.subscription?.unsubscribe();
  delete activeSubscriptions[subscriptionId];
};

const untrackAllSubscriptions = ({ baseDomain }) => {
  if (!apolloClients[baseDomain]) return;

  const { activeSubscriptions } = apolloClients[baseDomain];
  Object.keys(activeSubscriptions).forEach((key) => {
    activeSubscriptions?.[key]?.subscription?.unsubscribe();
    delete activeSubscriptions[key];
  });
};

const removeApolloClient = ({ baseDomain = '' }) => {
  delete apolloClients[baseDomain];
};

const removeAllApolloClients = () => {
  Object.keys(apolloClients).forEach((key) => {
    delete apolloClients[key];
  });
};

export {
  apolloClients,
  getApolloClient,
  removeApolloClient,
  removeAllApolloClients,
  getActiveSubscriptions,
  trackSubscription,
  untrackSubscription,
  untrackAllSubscriptions,
};
